import React, { useEffect, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Collapse,
  Divider,
  useMediaQuery,
  IconButton,
  Badge,
  // Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setPreBook } from "../../../redux/slices/authSlice";
import { getPreBookAvailability } from "../../../redux/apis/retailer/preBookApis";
import { getSalesPreBookAvailability } from "../../../redux/apis/sales/preBookApis";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../redux/store";
import { useChatUnread } from "../../../pages/chat/useChatUnread";
import {
  getNavigationConfig,
  isParentActive,
  adminNavigationSections,
  salesNavigationSections,
  retailerNavigationSections,
  NavigationItem,
} from "../../../config/navigationConfig";
import { useStampEnabled } from "../../../hooks/useStampEnabled";
import woopsaLogo from "../../../assets/Vector.svg";
import rabbitLogo from "../../../assets/Rabbit.svg";
import collapseIcon from "../../../assets/SidebarCollapse.svg";
import DashboardIcon from "../../../assets/dashboard.svg";
import OrderIcon from "../../../assets/icons/order.svg";
import PromoIcon from "../../../assets/icons/promo.svg";
import AccountReceivableIcon from "../../../assets/icons/account.svg";
import ContactIcon from "../../../assets/icons/contactUs.svg";
import ChatIcon from "../../../assets/icons/chat.svg";
import ProductLicenceIcon from "../../../assets/Menu Icon.svg";
import CustomersIcon from "../../../assets/Menu Icon (6).svg";
import ProductsIcon from "../../../assets/Menu Icon (5).svg";
import VendorsIcon from "../../../assets/Menu Icon (1).svg";
import ReportsIcon from "../../../assets/Menu Icon (2).svg";
import PurchaseOrdersIcon from "../../../assets/Menu Icon (3).svg";
import BackOfficeIcon from "../../../assets/Menu Icon (4).svg";
import SettingsIcon from "../../../assets/Menu Icon (4).svg";
import TrackDevicesIcon from "../../../assets/trackdevice.svg";
import UserManagementIcon from "../../../assets/usermanagement.svg";
import PoliciesIcon from "../../../assets/policies.svg";
import OrderedItemsIcon from "../../../assets/orderItems.svg";
import OrderHistoryIcon from "../../../assets/orderHistory.svg";
import NotificationsIcon from "../../../assets/notification 7.svg";
import CalendarMonthIcon from "../../../assets/calender.svg";
import SupportTicketsIcon from "../../../assets/support.svg";
import ProductCatalogueIcon from "../../../assets/productCatalogue.svg";
import LinkIcon from "../../../assets/link.svg";
import RetailerRequestsIcon from "../../../assets/icons/licence-active.svg";
import EpickSettingIcon from "../../../assets/epick_setting.svg";
import InventoryIcon from "../../../assets/itemsGlobal.svg";
import ReportsAnalyticsIcon from "../../../assets/Menu Icon.svg";
import DefinitionIcon from "../../../assets/Definition Icon.svg";
import TradeshowManagementIcon from "../../../assets/Tradeshow Management.svg";
import CallListIcon from "../../../assets/Call List.svg";
import DayCloseIcon from "../../../assets/Update(End-of-day).svg";

const iconMap: { [key: string]: string } = {
  dashboard: DashboardIcon,
  order: OrderIcon,
  promo: PromoIcon,
  "tradeshow-management": TradeshowManagementIcon,
  account: AccountReceivableIcon,
  contactUs: ContactIcon,
  chat: ChatIcon,
  file: ProductLicenceIcon,
  customers: CustomersIcon,
  products: ProductsIcon,
  orders: OrderIcon,
  vendors: VendorsIcon,
  reports: ReportsIcon,
  "purchase-orders": PurchaseOrdersIcon,
  "back-office": BackOfficeIcon,
  "track-login-devices": TrackDevicesIcon,
  settings: SettingsIcon,
  "setup-definitions": DefinitionIcon,
  "user-management": UserManagementIcon,
  "terms-and-conditions": ProductLicenceIcon,
  "privacy-policy": ProductLicenceIcon,
  "disclaimer": ProductLicenceIcon,
  "return-policy": ProductLicenceIcon,
  policies: PoliciesIcon,
  "ordered-items": OrderedItemsIcon,
  "order-history": OrderHistoryIcon,
  retailers: CustomersIcon,
  notifications: NotificationsIcon,
  CalendarMonthIcon: CalendarMonthIcon,
  SupportTicketsIcon: SupportTicketsIcon,
  "product-catalog": ProductCatalogueIcon,
  "links": LinkIcon,
  "retailer-requests": RetailerRequestsIcon,
  "e-pick-settings": EpickSettingIcon,
  "inventory": InventoryIcon,
  "reports-analytics": ReportsAnalyticsIcon,
  "driver-management": UserManagementIcon,
  orderChecker: OrderIcon,
  "call-list": CallListIcon,
  "msa": ReportsIcon,
  "ar-entry": AccountReceivableIcon,
  "account-payable": AccountReceivableIcon,
  "day-close": DayCloseIcon,
  "create-po": PurchaseOrdersIcon,
  "stamp-entry": InventoryIcon,
};

// Parent nav items that expand into sub-links, across all roles' sections.
// A group should be open when the route is on the parent or any of its children.
const expandableParents = [
  ...adminNavigationSections,
  ...salesNavigationSections,
  ...retailerNavigationSections,
]
  .flatMap((section) => section.items)
  .filter((item) => item.children?.length);

const openGroupsForPath = (pathname: string): { [name: string]: boolean } => {
  const open: { [name: string]: boolean } = {};
  expandableParents.forEach((item) => {
    open[item.name] = [item.path, ...item.children!.map((c) => c.path)].some(
      (p) => isParentActive(pathname, p.split("?")[0])
    );
  });
  return open;
};

interface SidebarProps {
  variant: "horizontal" | "vertical";
  open?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
  // Collapse/expand toggle rendered in the sidebar's own header (desktop).
  onToggle?: () => void;
  // Fired for any mouse movement over the drawer; the layout uses it to reset
  // its auto-collapse inactivity timer.
  onMouseMove?: React.MouseEventHandler<HTMLDivElement>;
  // Hover-expand: the layout starts a delayed expand of the collapsed drawer
  // on enter (desktop only); closing is handled by the auto-collapse timer.
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  // Leaving the drawer cancels a pending hover-expand in the layout.
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  // Clicking anywhere on the collapsed drawer expands it instantly.
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

const Sidebar: React.FC<SidebarProps> = ({
  variant,
  open = true,
  onClose,
  collapsed = false,
  onToggle,
  onMouseMove,
  onMouseEnter,
  onMouseLeave,
  onClick,
}) => {
  // Whether the pointer is anywhere over the drawer — drives the collapsed
  // header's rabbit → collapse-icon flip.
  const [hovered, setHovered] = useState(false);
  const isMobile = useMediaQuery("(max-width:1199px)");
  const location = useLocation();
  const { t } = useTranslation();
  const mode = useSelector((state: RootState) => state.theme.mode);
  // Unread badge on the "Chat" nav item (retailer + sales; the admin has none).
  const { role, module, showTradeShow, priceBook, preBook, token, selectedCustomer } = useSelector((state: RootState) => state.auth);
  const chatUnread = useChatUnread(role);
  const salesCustomerNumber = selectedCustomer?.C_Number;
  const dispatch = useDispatch();
  // Retailer: ask once per login / store switch (new token) whether any pre-book is open
  // to this customer. Drives the "Pre-Book" nav item below.
  useEffect(() => {
    if (role !== "retailer" || !token) return;
    let cancelled = false;
    getPreBookAvailability()
      .then((res: any) => {
        if (!cancelled) dispatch(setPreBook(res?.data?.available === true));
      })
      .catch(() => {
        if (!cancelled) dispatch(setPreBook(false));
      });
    return () => {
      cancelled = true;
    };
  }, [role, token, dispatch]);
  // Sales: same check per selected customer (S1 takes C_Number as a query param,
  // so it works before / after the customer is picked into the session).
  useEffect(() => {
    if (role !== "sales" || !token) return;
    if (!salesCustomerNumber) {
      dispatch(setPreBook(false));
      return;
    }
    let cancelled = false;
    getSalesPreBookAvailability(salesCustomerNumber)
      .then((res: any) => {
        if (!cancelled) dispatch(setPreBook(res?.data?.available === true));
      })
      .catch(() => {
        if (!cancelled) dispatch(setPreBook(false));
      });
    return () => {
      cancelled = true;
    };
  }, [role, token, salesCustomerNumber, dispatch]);
  // Feature-level gate: whether Stamp Inventory (Option 4003) is on. Hides the
  // "Stamp Entry" nav item below when the feature is switched off.
  const stampEnabled = useStampEnabled();
  // Translate the nav label for display only. The English `item.name` stays the
  // source of truth for routing/filtering logic; non-retailer roles fall back to
  // English via defaultValue, so they are unaffected.
  const navLabel = (name: string) => t(`sidebar.${name}`, { defaultValue: name });
  // Get role-based navigation config
  const salesNavigationConfig = getNavigationConfig(role).filter((item) => item.check === "sales");
  const filterByRoleModule = salesNavigationConfig.filter((item) =>
    // TEMP: "Chat" bypasses the permission-module check for the demo — the
    // backend has no "Chat" module yet. Once User Management supports it,
    // remove "Chat" from this list so it's permission-controlled again.
    ["Policies", "Trade Show", "Price Book", "Chat", "Pre-Book"].includes(item.name) ||
    module?.some((moduleItem: any) => moduleItem.module === item.name && moduleItem?.view === true)
  );
  let navigationConfig = role === "sales" ? filterByRoleModule : getNavigationConfig(role);
  // For retailer and sales: show "Trade Show" tab only when showTradeShow is true
  if ((role === "retailer" || role === "sales") && !showTradeShow) {
    navigationConfig = navigationConfig.filter((item) => item.name !== "Trade Show");
  }
  // For sales and retailer: show Price Book nav only when priceBook is true
  if ((role === "sales" || role === "retailer") && priceBook !== true) {
    navigationConfig = navigationConfig.filter(
      (item) => item.name !== "Price Book" && item.name !== "Price Book Cart"
    );
  }
  // Retailer / sales: show "Pre-Book" only when at least one pre-book is open to
  // this customer (sales: the customer selected in the session)
  if ((role === "retailer" || role === "sales") && !preBook) {
    navigationConfig = navigationConfig.filter((item) => item.name !== "Pre-Book");
  }
  // Hide "Stamp Entry" for everyone (admin included) when the Stamp Inventory
  // feature is turned off in Global Options (Option 4003). Unlike the other flags
  // this is a DB-wide setting, so it comes from GET /stampentry/status, not auth.
  if (!stampEnabled) {
    navigationConfig = navigationConfig.filter((item) => item.name !== "Stamp Entry");
  }

  // ── Grouped (sectioned) sidebar for the admin role ──────────────────────────
  // Expand/collapse state per parent item that has children (e.g. "Dashboard",
  // "Marketing"). A group counts as "on route" when the current path is the
  // parent's or any child's base path.
  const [openGroups, setOpenGroups] = useState<{ [name: string]: boolean }>(() =>
    openGroupsForPath(location.pathname)
  );

  // Keep expandable groups in sync with the route: auto-expand the group we
  // navigated into, auto-collapse the others. Only runs on pathname changes,
  // so manually toggling a group while staying on the same page still works.
  useEffect(() => {
    setOpenGroups((prev) => {
      const next = openGroupsForPath(location.pathname);
      const changed = Object.keys(next).some((name) => prev[name] !== next[name]);
      return changed ? { ...prev, ...next } : prev;
    });
  }, [location.pathname]);

  // Sections for the current role. Visibility is driven by `navigationConfig`
  // above, which already went through the exact same role/permission filters
  // as the old flat sidebar (sales module view flags, Trade Show, Price Book,
  // Stamp Entry) — sections only regroup whatever survived, so permission
  // behavior is unchanged; this is purely a UI regrouping.
  const sectionsForRole =
    role === "distributor"
      ? adminNavigationSections
      : role === "sales"
        ? salesNavigationSections
        : role === "retailer"
          ? retailerNavigationSections
          : null;
  const allowedNames = new Set(navigationConfig.map((item) => item.name));
  const visibleSections = (sectionsForRole || [])
    .map((section) => ({
      ...section,
      items: section.items.filter((item: any) => allowedNames.has(item.name)),
    }))
    .filter((section) => section.items.length > 0);

  const currentTab = new URLSearchParams(location.search).get("tab");
  // A dashboard sub-link is active when we're on its base path and its ?tab=
  // matches; with no tab in the URL the first tab is the one being shown.
  // Children without a ?tab= (e.g. Marketing's) are plain routes, active
  // whenever the current path is on them.
  const isSubItemActive = (subPath: string, index: number): boolean => {
    const [basePath, query] = subPath.split("?");
    const subTab = new URLSearchParams(query).get("tab");
    if (!subTab) return isParentActive(location.pathname, basePath);
    if (location.pathname !== basePath) return false;
    return currentTab ? currentTab === subTab : index === 0;
  };

  const selectedBg = mode === "dark" ? "#3a3a3a" : "#EDF4F9";
  const connectorColor = mode === "dark" ? "#4a4a4a" : "#E3E8EF";
  const dotColor = mode === "dark" ? "#666" : "#D2DAE3";

  const renderSectionItem = (item: NavigationItem) => {
    const hasChildren = !!item.children?.length;
    // A parent is highlighted on its own path or, for groups whose children are
    // separate routes (e.g. Marketing), on any child's path.
    const selected =
      isParentActive(location.pathname, item.path) ||
      !!item.children?.some((child) =>
        isParentActive(location.pathname, child.path.split("?")[0])
      );
    const isOpen = !!openGroups[item.name];
    return (
      <React.Fragment key={item.name}>
        <ListItem disablePadding sx={{ px: 1, py: 0.25 }}>
          <ListItemButton
            component={Link}
            to={item.path}
            onClick={() => {
              if (hasChildren) {
                setOpenGroups((prev) => ({ ...prev, [item.name]: !isOpen }));
              }
              if (isMobile && !hasChildren) onClose?.();
            }}
            sx={{
              width: "100%",
              minHeight: 44,
              borderRadius: "10px",
              justifyContent: collapsed ? "center" : "initial",
              px: collapsed ? 1.5 : 2,
              color: selected ? "primary.main" : "text.secondary",
              bgcolor: selected ? selectedBg : "transparent",
              transition: "padding 0.3s ease-in-out",
              "&:hover": { bgcolor: selected ? selectedBg : "action.hover" },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: collapsed ? 0 : 2,
                justifyContent: "center",
                color: selected ? "primary.main" : "inherit",
                transition: "margin-right 0.3s ease-in-out",
              }}
            >
              <Badge
                // Unread chat messages — retailer/sales only (see useChatUnread).
                badgeContent={item.name === "Chat" ? chatUnread : 0}
                max={99}
                color="error"
                overlap="circular"
                sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16 } }}
              >
                <img
                  src={iconMap[item.icon]}
                  alt={item.name}
                  style={{ width: 20, height: 20 }}
                />
              </Badge>
            </ListItemIcon>
            {/* Label stays mounted: it fades + squeezes with the drawer width
                instead of popping in/out (noWrap so it never reflows mid-way) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                minWidth: 0,
                maxWidth: collapsed ? 0 : 220,
                opacity: collapsed ? 0 : 1,
                overflow: "hidden",
                transition: "max-width 0.3s ease-in-out, opacity 0.2s ease",
              }}
            >
              <ListItemText
                primary={navLabel(item.name)}
                primaryTypographyProps={{
                  fontSize: 15,
                  fontWeight: selected ? 600 : 400,
                  noWrap: true,
                }}
                sx={{ my: 0 }}
              />
              {hasChildren &&
                (isOpen ? (
                  <KeyboardArrowUpIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                ))}
            </Box>
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isOpen && !collapsed} timeout="auto" unmountOnExit>
            <Box
              sx={{
                ml: "34px",
                mr: 1,
                my: 0.5,
                borderLeft: `2px solid ${connectorColor}`,
              }}
            >
              {item.children!.map((child, idx) => {
                const active = isSubItemActive(child.path, idx);
                return (
                  <ListItemButton
                    key={child.name}
                    component={Link}
                    to={child.path}
                    onClick={isMobile ? onClose : undefined}
                    sx={{
                      position: "relative",
                      minHeight: 36,
                      py: 0.5,
                      pl: 3,
                      borderRadius: "0 10px 10px 0",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: -5,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: active ? "primary.main" : dotColor,
                      }}
                    />
                    <ListItemText
                      primary={navLabel(child.name)}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: active ? 600 : 400,
                        color: active ? "primary.main" : "text.secondary",
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </Box>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  // Sidebar's own header: the Woopsa logo now lives inside the floating
  // sidebar card, with the collapse toggle (desktop) / close button (mobile).
  const sidebarHeader = (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: 58, lg: 66 },
        flexShrink: 0,
      }}
    >
      {/* Collapsed layer: rabbit mark, cross-faded with the full header.
          Hovering anywhere over the drawer flips the rabbit around to reveal
          the collapse/expand icon (click = instant expand). */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pt: 0.5,
          opacity: collapsed ? 1 : 0,
          pointerEvents: collapsed ? "auto" : "none",
          transition: "opacity 0.25s ease",
        }}
      >
        <Box
          onClick={onToggle}
          sx={{
            // Wide enough for the rabbit's rendered width (29.1px at h=24) so
            // margin:auto truly centers it instead of left-sticking.
            position: "relative",
            width: 30,
            height: 24,
            cursor: "pointer",
            perspective: "200px",
          }}
        >
          {/* Front face: rabbit */}
          <Box
            component="img"
            src={rabbitLogo}
            alt="Woopsa"
            sx={{
              position: "absolute",
              inset: 0,
              m: "auto",
              height: 24,
              backfaceVisibility: "hidden",
              // The glyph's ink is head-heavy to the right (centroid ~1.5px
              // right of its box center), so nudge left to center it optically.
              transform: hovered && collapsed
                ? "translateX(-1.5px) rotateY(180deg)"
                : "translateX(-1.5px) rotateY(0deg)",
              opacity: hovered && collapsed ? 0 : 1,
              transition: "transform 0.35s ease, opacity 0.2s ease",
            }}
          />
          {/* Back face: collapse/expand icon, revealed on hover */}
          <Box
            component="img"
            src={collapseIcon}
            alt="Expand sidebar"
            sx={{
              position: "absolute",
              inset: 0,
              m: "auto",
              width: 17,
              height: 16,
              backfaceVisibility: "hidden",
              transform: hovered && collapsed ? "rotateY(0deg)" : "rotateY(-180deg)",
              opacity: hovered && collapsed ? 1 : 0,
              transition: "transform 0.35s ease, opacity 0.2s ease",
            }}
          />
        </Box>
      </Box>
      {/* Expanded layer: full logo + collapse toggle */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: 0.5,
          opacity: collapsed ? 0 : 1,
          pointerEvents: collapsed ? "none" : "auto",
          transition: "opacity 0.25s ease",
          overflow: "hidden",
        }}
      >
        {/* Figma: 130 × 24 logo */}
        <Box
          component="img"
          src={woopsaLogo}
          alt="Woopsa"
          sx={{ width: 130, height: "auto", flexShrink: 0 }}
        />
        <IconButton
          size="small"
          onClick={isMobile ? onClose : onToggle}
          sx={{ color: "text.secondary" }}
        >
          {isMobile ? (
            <CloseIcon sx={{ fontSize: 20 }} />
          ) : (
            <Box
              component="img"
              src={collapseIcon}
              alt="Collapse sidebar"
              sx={{ width: 17, height: 16 }}
            />
          )}
        </IconButton>
      </Box>
    </Box>
  );

  const groupedDrawerContent = (
    <>
      <Box sx={{ pb: 2 }}>
        {visibleSections.map((section, sectionIdx) => (
          <Box key={section.title}>
            {/* Fixed-height header: the title cross-fades with the collapsed
                divider so rows never jump vertically during the animation */}
            <Box
              sx={{
                position: "relative",
                height: sectionIdx === 0 ? 38 : 42,
                display: "flex",
                alignItems: "flex-end",
              }}
            >
              <Box
                sx={{
                  px: 3,
                  pb: 0.75,
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  color: mode === "dark" ? "#8a94a0" : "#8A94A6",
                  opacity: collapsed ? 0 : 1,
                  transition: "opacity 0.2s ease",
                }}
              >
                {t(`sidebar.section.${section.title}`, { defaultValue: section.title })}
              </Box>
              {sectionIdx > 0 && (
                <Divider
                  sx={{
                    position: "absolute",
                    left: 12,
                    right: 12,
                    top: "50%",
                    opacity: collapsed ? 1 : 0,
                    transition: "opacity 0.2s ease",
                  }}
                />
              )}
            </Box>
            <List sx={{ p: 0 }}>{section.items.map(renderSectionItem)}</List>
          </Box>
        ))}
      </Box>
    </>
  );

  const drawerContent = (
    <>
      <List
        sx={{
          p: 0,
          display: variant === "horizontal" ? "flex" : "block",
          flexDirection: variant === "horizontal" ? "row" : "column",
        }}
      >
        {navigationConfig.map((item, index) => {
          const selected = isParentActive(location.pathname, item.path);
          return (
            <ListItem
              key={item.name}
              disablePadding
              sx={{
                width: variant === "horizontal" ? "auto" : "100%",
                display: "flex",
                justifyContent:
                  variant === "horizontal" ? "center" : "flex-start",
                marginTop: index === 0 ? isMobile ? 0 : 1 : 0,
                borderLeft: selected ? `3px solid ${mode === "dark" ? "#444" : "#3C7795"}` : "none",
                borderRadius: "0 10px 10px 0",
              }}
            >
              <ListItemButton
                component={Link}
                to={item.path}
                onClick={isMobile ? onClose : undefined}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? "center" : "initial",
                  px: collapsed ? 2 : 3,
                  color: selected ? "primary.main" : "text.secondary",
                  flexDirection: "row",
                  transition: "padding 0.3s ease-in-out",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed || variant === "horizontal" ? 0 : 2,
                    justifyContent: "center",
                    color: selected ? "primary.main" : "inherit",
                    transition: "margin-right 0.3s ease-in-out",
                  }}
                >
                  <Badge
                    // Unread chat messages — retailer/sales only (see useChatUnread).
                    badgeContent={item.name === "Chat" ? chatUnread : 0}
                    max={99}
                    color="error"
                    overlap="circular"
                    sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16 } }}
                  >
                    <img
                      src={iconMap[item.icon]}
                      alt={item.name}
                      style={{ width: 20, height: 20 }}
                    />
                  </Badge>
                  {/* <img
                    src={iconMap[item.icon]}
                    alt={item.name}
                    style={{ width: 20, height: 20 }}
                  /> */}
                </ListItemIcon>
                {variant !== "horizontal" && (
                  <ListItemText
                    primary={navLabel(item.name)}
                    primaryTypographyProps={{
                      fontWeight: selected ? 500 : 400,
                      noWrap: true,
                    }}
                    sx={{
                      my: 0,
                      fontSize: "16px",
                      fontWeight: selected ? 500 : 400,
                      maxWidth: collapsed ? 0 : 220,
                      opacity: collapsed ? 0 : 1,
                      overflow: "hidden",
                      transition: "max-width 0.3s ease-in-out, opacity 0.2s ease",
                    }}
                  />
                )}

                {variant === "horizontal" && (
                  <ListItemText
                    primary={navLabel(item.name)}
                    primaryTypographyProps={{
                      fontSize: 12,
                      mt: 0.5,
                      textAlign: "center",
                      fontWeight: selected ? 500 : 400,
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </>
  );

  if (variant === "horizontal") {
    return (
      <Box
        sx={{
          width: "100%",
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          height: 48,
          display: "flex",
          alignItems: "center",
          px: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            width: "100%",
            justifyContent: "flex-start",
            alignItems: "center",
            maxWidth: "1600px",
            overflow:"auto",
            mx: "auto",
          }}
        >
          {navigationConfig.map((item) => {
            const selected = isParentActive(location.pathname, item.path);

            return (
              <Box
                key={item.name}
                component={Link}
                to={item.path}
                onClick={isMobile ? onClose : undefined}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  textDecoration: "none",
                  color: selected ? "primary.main" : "text.secondary",
                  fontWeight: selected ? 600 : 400,
                  bgcolor: selected ? "action.selected" : "transparent",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: "action.hover",
                    color: "primary.main",
                  },
                }}
              >
                <img
                  src={iconMap[item.icon]}
                  alt={item.name}
                  style={{ width: 20, height: 20 }}
                />
                <Box
                  component="span"
                  sx={{
                    fontSize: 14,
                    whiteSpace: "nowrap",
                  }}
                >
                  {navLabel(item.name)}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  const drawerWidth = collapsed ? 65 : 260;

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={open}
      onClose={onClose}
      onMouseMove={onMouseMove}
      onMouseEnter={(e) => {
        setHovered(true);
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setHovered(false);
        onMouseLeave?.(e);
      }}
      onClick={onClick}
      sx={{
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          // Desktop: floating rounded card with a 16px margin all around
          // (Figma spec — top aligns with the navbar card); mobile keeps the
          // full-height temporary drawer.
          top: isMobile ? 0 : 16,
          left: isMobile ? 0 : 16,
          height: isMobile ? "100vh" : "calc(100% - 32px)",
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.3s ease-in-out",
          borderRadius: isMobile ? 0 : "12px",
          // Figma: no border on the floating card, only a soft shadow
          // (0 0 4px #0D0012 @ 6%). Mobile keeps the plain full-height drawer.
          // Dark mode: the card sits on a same-black page, so a hairline border
          // outlines it (light mode stays borderless).
          border: !isMobile && mode === "dark" ? "1px solid #2A2A33" : "none",
          borderRight: isMobile
            ? `${mode === "dark" ? "1px solid #444" : "none"}`
            : undefined,
          boxShadow: isMobile ? "none" : "0px 0px 4px 0px #0D00120F",
        },
      }}
    >
      {sidebarHeader}
      {/* Sidebar-only override of the global blue scrollbar: gray thumb */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          // NOTE: no `scrollbarColor` here — setting the standard property makes
          // Chrome ignore the global ::-webkit-scrollbar styling and render a
          // native (thick, arrowed) scrollbar. Webkit overrides only.
          "&::-webkit-scrollbar-thumb": { background: "#B0B0B8" },
          "&&::-webkit-scrollbar-thumb:hover": { background: "#9A9AA3" },
        }}
      >
        {sectionsForRole ? groupedDrawerContent : drawerContent}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
